import { useState } from 'react';
import { BiEnvelope, BiTime, BiTrash, BiUser } from 'react-icons/bi';
import { BsThreeDots } from 'react-icons/bs';
import { FaPlus, FaRegMessage } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

import type { FileDto } from '@/api/api.dto';
import { imageVariantLink } from '@/common/media';
import { DeclinedBadge } from '@/components/DeclinedBadge/DeclinedBadge';
import { PendingBadge } from '@/components/PendingBadge/PendingBadge';
import { useInviteRights } from '@/hooks/useInviteRights';
import { useAuth } from '@/store/auth/auth.store';
import { useChats } from '@/store/chats/chats.store';
import { getParticipantDeclineReason, getParticipantStatus } from '@/store/events/events.dto';
import type { EventResponse } from '@/store/events/events.dto';
import type { PendingGuestInvite } from '@/store/invites/invites.store';
import type { EventInviteAnswer } from '@/store/invites/invites.types';
import { formatEventDateTime } from '@/utils/date';
import { formatDate } from '@/utils/formatDate';
import { canViewGuestProfile } from '@/utils/roles';

import { Avatar } from '../Avatar';
import { ConfirmationModal } from '../ConfirmationModal';
import ContextMenu from '../ContextMenu/ContextMenu';
import { GlassButton } from '../GlassButton';

import s from './GuestsSection.module.scss';
import { InviteDeadlineModal } from './InviteDeadlineModal';

interface Guest {
  id: string;
  avatar?: FileDto | null;
  person_info: { first_name: string; last_name: string };
}

interface GuestsSectionProps {
  guests: Guest[];
  participants: EventResponse['participants'];
  isOwnerOrCaptain: boolean;
  currentUserId: string | undefined;
  showInvite: boolean;
  onInviteClick: () => void;
  pendingInvites?: PendingGuestInvite[];
  /** Takes a guest off the event. The row menu is offered only where this is passed. */
  onRemoveGuest?: (guestId: string) => void;
  /** Withdraws an invitation nobody has answered yet. */
  onCancelInvite?: (invite: PendingGuestInvite) => void;
  onResendInvite?: (invite: PendingGuestInvite) => void;
  /** Answers from invitees who have not registered yet. */
  answers?: EventInviteAnswer[];
  /** Takes an invitee who answered without an account off the event. */
  onRemoveAnswer?: (answer: EventInviteAnswer) => void;
  /** Shows when each pending invitation has to be answered by; guests don't see it. */
  showDeadlines?: boolean;
  /** Moves a pending invitation's deadline. The row menu offers it only where this is passed. */
  onChangeDeadline?: (invite: PendingGuestInvite, expiresAt: string) => Promise<void>;
  /** The event's end: the latest deadline that may be picked. */
  deadlineMax?: Date | null;
}

interface RowActions {
  name: string;
  /** "Cancel invitation" until the guest has answered, "Remove guest" after. */
  removeLabel: string;
  confirmTitle: string;
  confirmMessage: string;
  onRemove: () => void;
  onResend?: () => void;
  onChangeDeadline?: () => void;
  profileId?: string;
}

export function GuestsSection({
  guests,
  participants,
  isOwnerOrCaptain,
  currentUserId,
  showInvite,
  onInviteClick,
  pendingInvites = [],
  onRemoveGuest,
  onCancelInvite,
  onResendInvite,
  answers = [],
  onRemoveAnswer,
  showDeadlines = false,
  onChangeDeadline,
  deadlineMax,
}: GuestsSectionProps) {
  const navigate = useNavigate();
  // The row menus of invitations are for whoever may still invite and handle this one.
  const { canManage } = useInviteRights();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: RowActions } | null>(null);
  const [toConfirm, setToConfirm] = useState<RowActions | null>(null);
  const [deadlineFor, setDeadlineFor] = useState<PendingGuestInvite | null>(null);
  const { getOrCreateOneToOneRoom } = useChats();
  const userRoles = useAuth((state) => state.session?.userRoles);
  // A guest's profile is the crew's to read, and only once the guest has accepted — an
  // outstanding invitation says nothing about who is actually coming aboard.
  const canOpenProfiles = canViewGuestProfile(userRoles);

  const handleMessageGuest = async (userId: string) => {
    const { id } = await getOrCreateOneToOneRoom(userId);
    navigate(`/inbox/${id}`);
  };

  const actionsButton = (row: RowActions) => (
    <GlassButton
      size={32}
      shape="square"
      aria-label={`Actions for ${row.name}`}
      aria-haspopup="menu"
      onClick={(e) => setMenu({ anchor: e.currentTarget, row })}
    >
      <BsThreeDots size={16} />
    </GlassButton>
  );

  const answerRow = (a: EventInviteAnswer) => (
    <div className={`${s.guestItem}${a.accepted ? '' : ` ${s.declined}`}`} key={`answer-${a.id}`}>
      <div className={s.guestItemInfo}>
        <Avatar firstName={a.name} size={24} />
        <span className={s.guestItemText}>
          {a.name}
          <span className={s.guestItemMeta}>
            {a.accepted ? 'Accepted' : 'Declined'} {formatDate(a.answeredAt)}
          </span>
        </span>
      </div>
      <div className={s.guestItemRight}>
        {isOwnerOrCaptain && !a.accepted && <DeclinedBadge reason={a.declineReason} />}
        {canManage(a.creatorId) &&
          onRemoveAnswer &&
          actionsButton({
            name: a.name,
            removeLabel: 'Remove Guest',
            confirmTitle: 'Remove this guest?',
            confirmMessage: `${a.name} will be taken off this event.`,
            onRemove: () => onRemoveAnswer(a),
          })}
      </div>
    </div>
  );

  return (
    <div className={s.guestsSection}>
      {guests.length > 0 || pendingInvites.length > 0 || answers.length > 0 ? (
        <div className={s.guestsList}>
          {guests
            .sort((g1, g2) => {
              const s1 = getParticipantStatus(participants, g1.id);
              const s2 = getParticipantStatus(participants, g2.id);

              if (s1 === 'declined' && s2 !== 'declined') return 1;
              if (s1 !== 'declined' && s2 === 'declined') return -1;
              return 0;
            })
            .map((g) => {
              const guestStatus = getParticipantStatus(participants, g.id);
              const declineReason = getParticipantDeclineReason(participants, g.id);
              const name = `${g.person_info.first_name} ${g.person_info.last_name}`.trim();
              const answered = guestStatus === 'accepted' || guestStatus === 'declined';
              return (
                <div
                  className={`${s.guestItem}${guestStatus === 'declined' ? ` ${s.declined}` : ''}`}
                  key={g.id}
                >
                  {canOpenProfiles && guestStatus === 'accepted' ? (
                    <button
                      type="button"
                      className={s.guestItemLink}
                      onClick={() => navigate(`/guests/${g.id}`)}
                    >
                      <Avatar
                        src={imageVariantLink(g.avatar, 24)}
                        alt={g.person_info.first_name}
                        size={24}
                      />
                      {g.person_info.first_name} {g.person_info.last_name}
                    </button>
                  ) : (
                    <div className={s.guestItemInfo}>
                      <Avatar
                        src={imageVariantLink(g.avatar, 24)}
                        alt={g.person_info.first_name}
                        size={24}
                      />
                      {g.person_info.first_name} {g.person_info.last_name}
                    </div>
                  )}
                  <div className={s.guestItemRight}>
                    {isOwnerOrCaptain && guestStatus === 'declined' && (
                      <DeclinedBadge reason={declineReason} />
                    )}
                    {isOwnerOrCaptain && (!guestStatus || guestStatus === 'unknown') && (
                      <PendingBadge />
                    )}
                    {currentUserId !== g.id && (
                      <GlassButton
                        size={32}
                        shape="square"
                        aria-label={`Message ${g.person_info.first_name}`}
                        onClick={() => handleMessageGuest(g.id)}
                      >
                        <FaRegMessage size={16} />
                      </GlassButton>
                    )}
                    {isOwnerOrCaptain &&
                      onRemoveGuest &&
                      currentUserId !== g.id &&
                      actionsButton({
                        name,
                        removeLabel: answered ? 'Remove Guest' : 'Cancel Invitation',
                        confirmTitle: answered
                          ? 'Remove Guest?'
                          : `Cancel this invitation to ${name}?`,
                        confirmMessage: answered
                          ? `${name} will be taken off this event.`
                          : `${name} will no longer be invited to this event.`,
                        onRemove: () => onRemoveGuest(g.id),
                        profileId: canOpenProfiles && guestStatus === 'accepted' ? g.id : undefined,
                      })}
                  </div>
                </div>
              );
            })}
          {answers.filter((a) => a.accepted).map(answerRow)}
          {pendingInvites.map((p) => (
            <div className={s.guestItem} key={`pending-${p.id}`}>
              <div className={s.guestItemInfo}>
                <Avatar firstName={p.name} size={24} />
                <span className={s.guestItemText}>
                  {p.name}
                  <span className={s.guestItemMeta}>Sent {formatDate(p.createdAt)}</span>
                  {showDeadlines && p.expiresAt && (
                    <span className={s.guestItemMeta}>
                      RSVP by {formatEventDateTime(p.expiresAt)}
                    </span>
                  )}
                </span>
              </div>
              <div className={s.guestItemRight}>
                <PendingBadge />
                {canManage(p.creatorId) &&
                  onCancelInvite &&
                  actionsButton({
                    name: p.name,
                    removeLabel: 'Cancel Invitation',
                    confirmTitle: `Cancel this invitation to ${p.name}?`,
                    confirmMessage: `${p.name} will no longer be invited to this event.`,
                    onRemove: () => onCancelInvite(p),
                    onResend: onResendInvite && (() => onResendInvite(p)),
                    onChangeDeadline: onChangeDeadline && (() => setDeadlineFor(p)),
                  })}
              </div>
            </div>
          ))}
          {answers.filter((a) => !a.accepted).map(answerRow)}
        </div>
      ) : (
        <p className={s.guestsEmptyState}>Please invite a guest to get started.</p>
      )}
      {showInvite && (
        <div className={s.guestsActions}>
          <button type="button" className={s.inviteGuestButton} onClick={onInviteClick}>
            <span className={s.inviteIcon}>
              <FaPlus />
            </span>
            Invite Guest
          </button>
        </div>
      )}
      <ContextMenu
        isOpen={!!menu}
        onClose={() => setMenu(null)}
        anchorEl={menu?.anchor}
        items={
          menu
            ? [
                ...(menu.row.onResend
                  ? [
                      {
                        label: 'Resend Invite',
                        icon: <BiEnvelope size={14} />,
                        onClick: menu.row.onResend,
                      },
                    ]
                  : []),
                ...(menu.row.onChangeDeadline
                  ? [
                      {
                        label: 'Change RSVP Deadline',
                        icon: <BiTime size={14} />,
                        onClick: menu.row.onChangeDeadline,
                      },
                    ]
                  : []),
                ...(menu.row.profileId
                  ? [
                      {
                        label: 'View Guest Details',
                        icon: <BiUser size={14} />,
                        onClick: () => navigate(`/guests/${menu.row.profileId}`),
                      },
                    ]
                  : []),
                {
                  label: menu.row.removeLabel,
                  icon: <BiTrash size={14} />,
                  variant: 'danger' as const,
                  onClick: () => setToConfirm(menu.row),
                },
              ]
            : []
        }
      />
      <ConfirmationModal
        open={!!toConfirm}
        onClose={() => setToConfirm(null)}
        onConfirm={() => toConfirm?.onRemove()}
        title={toConfirm?.confirmTitle ?? ''}
        message={toConfirm?.confirmMessage ?? ''}
        confirmText={toConfirm?.removeLabel}
        cancelText="Keep it"
      />
      {onChangeDeadline && (
        <InviteDeadlineModal
          invite={deadlineFor}
          onClose={() => setDeadlineFor(null)}
          onSave={onChangeDeadline}
          maxDate={deadlineMax}
        />
      )}
    </div>
  );
}
